import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { ItemForm, formWeightsToMg, type ItemFormValues } from './item-form';
import { useCreateItem } from '@/hooks/use-items';
import { useT } from '@/i18n/use-t';

const EMPTY: ItemFormValues = {
  sku: '',
  name: '',
  description: '',
  categoryId: '',
  unitOfMeasure: 'each',
  taxCategory: 'standard',
  ageRestriction: 0,
  isVariableWeight: false,
  skipBaggingAllowed: false,
  isMarked: false,
  exciseStampCount: 1,
  weightCheckMode: 'presence',
  grossWeightGrams: '',
  netWeightGrams: '',
  status: 'active',
  barcodes: [],
  attributes: [],
};

export function ItemNewPage() {
  const t = useT();
  const navigate = useNavigate();
  const create = useCreateItem();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('catalog.items.new', 'New item')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('catalog.itemNew.subtitle', 'Add a product to the catalog.')}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/catalog/items">{t('common.cancel', 'Cancel')}</Link>
        </Button>
      </header>

      <ItemForm
        defaultValues={EMPTY}
        submitLabel={t('catalog.itemNew.submit', 'Create item')}
        busy={create.isPending}
        errorText={error}
        onSubmit={async (values) => {
          setError(null);
          try {
            const {
              grossWeightGrams: _gross,
              netWeightGrams: _net,
              weightCheckMode,
              ...rest
            } = values;
            const created = await create.mutateAsync({
              ...rest,
              // SSC-109: send the mode only when the operator changed it, so
              // an untouched default stays "not chosen" on the server.
              ...(weightCheckMode === EMPTY.weightCheckMode ? {} : { weightCheckMode }),
              ...formWeightsToMg(values),
              description: values.description === '' ? undefined : values.description,
            });
            navigate(`/catalog/items/${created.id}`);
          } catch (err) {
            setError(extractError(err));
          }
        }}
      />
    </div>
  );
}

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string; issues?: { path: string; message: string }[] }
      | undefined;
    if (data?.issues !== undefined && data.issues.length > 0) {
      return data.issues.map((i) => `${i.path}: ${i.message}`).join('; ');
    }
    return data?.message ?? err.message;
  }
  return 'Unexpected error';
}
